/**
 * RhythmTimeline.tsx - Visual timeline for Rhythm mode
 * 
 * Displays a vertical timeline on the side with:
 * - Moving NEUTRAL marks (no color info, just timing)
 * - Central marker box (when mark reaches this, tap!)
 * - Synchronized with audio/game clock
 * 
 * CRITICAL: Timeline shows WHEN to tap, NOT WHAT color to tap
 * Player must remember the sequence from showSequence phase
 */

import { useEffect, useState, useRef } from 'react';
import { BeatEvent } from '../core/RhythmEngine';
import './RhythmTimeline.css';

interface RhythmTimelineProps {
  events: BeatEvent[]; // All tap target events (timing marks)
  currentTime: number; // Current game time
  lookAheadMs?: number; // How far ahead to show (default 3000ms)
  onEventReached?: (event: BeatEvent) => void;
}

// NEUTRAL colors - no specific button color info
const NEUTRAL_MARK_COLOR = 'linear-gradient(135deg, #E0E0E0 0%, #F5F5F5 100%)';
const NEUTRAL_BORDER_COLOR = '#BDBDBD';

export const RhythmTimeline = ({
  events,
  currentTime,
  lookAheadMs = 3000,
  onEventReached,
}: RhythmTimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [reachedEvents, setReachedEvents] = useState<Set<number>>(new Set());
  
  // Timeline dimensions
  const TIMELINE_HEIGHT = 400; // px
  const MARKER_Y = TIMELINE_HEIGHT / 2; // Center marker position
  const EVENT_HEIGHT = 20; // Height of event blocks
  
  /**
   * Calculate Y position for an event based on time
   * Events start at bottom and move up
   * When Y = MARKER_Y, it's time to tap
   */
  const getEventY = (eventTime: number): number => {
    const timeUntilEvent = eventTime - currentTime;
    
    // Map time to Y position
    // timeUntilEvent = 0 → Y = MARKER_Y (center)
    // timeUntilEvent = lookAheadMs → Y = TIMELINE_HEIGHT (bottom)
    // timeUntilEvent < 0 → Y < MARKER_Y (above, past events)
    
    const ratio = timeUntilEvent / lookAheadMs;
    const y = MARKER_Y + (ratio * (TIMELINE_HEIGHT - MARKER_Y));
    
    return y;
  };
  
  /**
   * Filter visible events (within look-ahead window)
   */
  const visibleEvents = events.filter(event => {
    const timeUntilEvent = event.time - currentTime;
    return timeUntilEvent >= -500 && timeUntilEvent <= lookAheadMs;
  });
  
  /**
   * Check for events that have reached the marker
   */
  useEffect(() => {
    visibleEvents.forEach((event, index) => {
      const y = getEventY(event.time);
      const isAtMarker = Math.abs(y - MARKER_Y) < 10; // Within 10px of marker
      
      if (isAtMarker && !reachedEvents.has(index) && onEventReached) {
        onEventReached(event);
        setReachedEvents(prev => new Set(prev).add(index));
      }
    });
  }, [currentTime, visibleEvents]);
  
  return (
    <div className="rhythm-timeline" ref={timelineRef}>
      {/* Timeline track */}
      <div className="timeline-track"></div>
      
      {/* Central marker box */}
      <div 
        className="timeline-marker" 
        style={{ top: `${MARKER_Y - 15}px` }}
      >
        <div className="marker-line"></div>
        <div className="marker-label">TAP</div>
      </div>
      
      {/* NEUTRAL event marks - NO COLOR INFORMATION */}
      {visibleEvents.map((event, index) => {
        const y = getEventY(event.time);
        const isPast = y < MARKER_Y - EVENT_HEIGHT;
        
        return (
          <div
            key={`${event.time}-${index}`}
            className={`timeline-event ${isPast ? 'past' : ''}`}
            style={{
              top: `${y - EVENT_HEIGHT / 2}px`,
              background: NEUTRAL_MARK_COLOR,
              borderColor: NEUTRAL_BORDER_COLOR,
            }}
          >
            {/* No color indicator - just neutral mark */}
          </div>
        );
      })}
    </div>
  );
};
